import { useCallback, useEffect, useRef, useState } from 'react';
import { Toast, useToast } from '@zillow/constellation-10';
import type { FileRejections, ZDocsUpload } from '@zillow/zdocs-web';
import {
  errorMessageFromCode,
  FileManager,
  FileWorkflowManager,
  RejectionErrorCode,
  ZDocsUploader,
} from '@zillow/zdocs-web';
import type { CtaAction } from '@zg-rentals/ts-your-rental-api-client';

const ACCEPTED_FILES = ['application/pdf', 'image/jpeg', 'image/png', 'image/heic'];

const CONTENT = {
  UPLOAD_SUCCESS: "Document linked! Your renter's insurance is now on file and we will notify irene@gmail.com.",
  REPLACE_SUCCESS: "Document updated! Your renter's insurance is now on file and we will notify irene@gmail.com.",
  UPLOAD_ERROR: 'Failed to upload document. Please try again.',
} as const;

type RentersInsuranceUploaderProps = {
  onUploadComplete?: (messageToken: string) => void;
  onUploadFailed?: () => void;
  shouldOpenFilePicker: boolean;
  onFilePickerOpened?: () => void;
  uploadAction: CtaAction | null;
};

const mockGenerateUploadToken = async (): Promise<string> => {
  return 'test-token';
};

export const RentersInsuranceUploader = ({
  onUploadComplete,
  onUploadFailed,
  shouldOpenFilePicker,
  onFilePickerOpened,
  uploadAction,
}: RentersInsuranceUploaderProps) => {
  const { enqueueToast } = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploaderKey, setUploaderKey] = useState(0);

  const onErrorToast = useCallback(
    (message: string) => {
      enqueueToast(<Toast tone="critical">{message}</Toast>);
    },
    [enqueueToast],
  );

  const handleUploadComplete = async (upload: ZDocsUpload) => {
    const messageToken = upload.messageToken || '';

    if (onUploadComplete) {
      onUploadComplete(messageToken);
    }

    const successMessage = CONTENT.UPLOAD_SUCCESS;
    enqueueToast(<Toast tone="success">{successMessage}</Toast>);
    setUploaderKey((prev) => prev + 1);
  };

  const handleUploadFailed = () => {
    onErrorToast(CONTENT.UPLOAD_ERROR);
    setUploaderKey((prev) => prev + 1);

    if (onUploadFailed) {
      onUploadFailed();
    }
  };

  const handleFileRejected = (rejections: FileRejections) => {
    const errorMessage = errorMessageFromCode(rejections.rejections[0]?.errorCodes[0] || RejectionErrorCode.Unknown);
    onErrorToast(errorMessage);
    setUploaderKey((prev) => prev + 1);
  };

  useEffect(() => {
    if (shouldOpenFilePicker && inputRef.current) {
      inputRef.current.click();
      if (onFilePickerOpened) {
        onFilePickerOpened();
      }
    }
  }, [shouldOpenFilePicker, onFilePickerOpened]);

  return (
    <ZDocsUploader
      key={uploaderKey}
      generateUploadToken={mockGenerateUploadToken}
      onUploadComplete={handleUploadComplete}
      onUploadFailed={handleUploadFailed}
      onFileRejected={handleFileRejected}
      accept={ACCEPTED_FILES}
      maxFiles={1}
      renderer={(props) => {
        const fileManagerRef = (node: HTMLDivElement | null) => {
          if (node) {
            const input = node.querySelector('input[type="file"]') as HTMLInputElement;
            if (input) {
              inputRef.current = input;
            }
          }
        };

        return (
          <div ref={fileManagerRef} style={{ display: 'none' }}>
            <FileManager {...props} />
            <FileWorkflowManager {...props} />
          </div>
        );
      }}
    />
  );
};

export default RentersInsuranceUploader;
